// "You are not looking at the real catalog." The banner a per-PR preview
// draws across the top of every page.
//
// It is drawn from what the BACKEND said answered the request
// (`BiDashboardListResponse.catalog`), never from the URL. The difference
// matters exactly once, and it is the case worth being right about: if the
// selector were ever dropped on the way to the API, a URL-driven banner would
// claim a preview while the page showed merged content — which is the one
// mistake this whole feature must not make, because the reader's next action
// is to merge.
//
// Warning tone, permanently visible, not dismissible. A reader who scrolls
// past it and then screenshots a card into Slack is the failure this exists to
// prevent, so it stays on the page rather than being a toast.
import type { BiCatalogInfo } from "@bi/shared/types";

import { isPreviewStale, leavePreview, previewCatalogLabel } from "../lib/preview-catalog";

export function PreviewBanner({ catalog }: { catalog: BiCatalogInfo }) {
  const skipped = catalog.skipped ?? [];
  return (
    <div className="bi-preview-banner" role="status">
      <div className="bi-preview-banner__line">
        <span className="bi-preview-banner__tag">Preview</span>
        <span>
          Showing the dashboard catalog from pull request {previewCatalogLabel(catalog.key)},
          against the dev warehouse. Nothing here is merged.
        </span>
        <button type="button" className="bi-preview-banner__leave" onClick={leavePreview}>
          Leave preview
        </button>
      </div>
      {/* THE LATEST PUSH DID NOT PARSE, so what is on screen is an EARLIER
          one's. The ingest keeps the last tree that parsed - re-downloading a
          tree that fails identically every five minutes is not worth it - and
          the consequence is a page that renders perfectly, with timestamps that
          look healthy, showing something that is not the branch. This is the
          only thing that tells the author, so it goes first and reads as an
          error rather than a note. */}
      {catalog.refusedError ? (
        <p className="bi-preview-banner__refused">
          The latest push did not parse, so this is an <strong>earlier</strong> push&apos;s
          catalog: {catalog.refusedError}
        </p>
      ) : null}
      {/* A skip in the environment's catalog is an incident somebody is paged
          about. A skip HERE is the author's own unmerged file, and they are
          the person reading this, so it is the most useful thing on the page:
          the tile they are looking for is missing because this file did not
          parse, not because the chart is wrong. */}
      {skipped.length > 0 ? (
        <ul className="bi-preview-banner__skips">
          {skipped.map((skip) => (
            <li key={skip.path}>
              <code>{skip.path}</code> did not parse, so it is not in this preview: {skip.error}
            </li>
          ))}
        </ul>
      ) : null}
      {/* TWO TIMESTAMPS. `syncedAt` (when this preview's tree last changed) is
          what the reader sees printed. `checkedAt` (when the ingest last
          looked) drives the stale warning: a preview can stop being reached
          (ingest cap, tick time budget, failing tick) while the page renders
          fine. Keying it off `syncedAt` would flag every paused pull request. */}
      {catalog.syncedAt ? (
        <p className="bi-preview-banner__stamp">
          Ingested {new Date(catalog.syncedAt).toLocaleString()}.{" "}
          {isPreviewStale(catalog.checkedAt)
            ? "Nothing has reached this preview in over fifteen minutes, so a recent push may not be here yet."
            : catalog.refusedError
              ? "The ingest is reaching this preview; it is the tree that will not parse. Fix the file above and push."
              : "A push reaches this page on the next five minute tick."}
        </p>
      ) : null}
    </div>
  );
}
